import { usePlannerResources, useResourceAvailabilityFull } from '@/domains/planner';
import type { CapacitySlot, LeaveBlock, PlannerResource } from '@skewb-works/contracts';
import { addDays, format, parseISO, startOfDay } from 'date-fns';
import { useMemo } from 'react';

export interface TeamDailyCapacity {
  date: string;
  totalHours: number;
  availableHours: number;
  utilizationPercent: number;
  isOverallocated: boolean;
  membersOnLeave: string[];
}

export interface TeamCapacitySummary {
  teamId: string;
  totalBookedHours: number;
  totalAvailableHours: number;
  averageUtilization: number;
  isOverallocated: boolean;
  overallocatedDays: number;
}

export interface MemberDailyCapacity {
  date: string;
  bookedHours: number;
  availableHours: number;
  utilizationPercent: number;
  isOnLeave: boolean;
}

export interface MemberCapacity {
  userId: number;
  userName: string;
  teamId: string;
  daily: Map<string, MemberDailyCapacity>;
  totalBookedHours: number;
  totalAvailableHours: number;
  averageUtilization: number;
  isOverallocated: boolean;
  isOnLeaveAllRange: boolean;
}

export interface UseTeamCapacitiesResult {
  /** Per-team daily capacity, keyed by teamId then dateStr. */
  capacitiesByTeam: Map<string, Map<string, TeamDailyCapacity>>;
  /** Per-team aggregates across the range. */
  summariesByTeam: Map<string, TeamCapacitySummary>;
  /** Per-team members with their own daily capacity. */
  membersByTeam: Map<string, MemberCapacity[]>;
  /** Overall numbers across all teams. */
  overall: {
    averageUtilization: number;
    teamsOverCapacity: number;
    totalTeams: number;
  };
  isLoading: boolean;
}

const HOURS_PER_USER_PER_DAY = 8;

function isUserOnLeaveOn(date: Date, leaves: LeaveBlock[]): boolean {
  const dayStart = startOfDay(date).getTime();
  const dayEnd = dayStart + 24 * 60 * 60 * 1000 - 1;
  return leaves.some((leave) => {
    const start = parseISO(leave.start).getTime();
    const end = parseISO(leave.end).getTime();
    return start <= dayEnd && end >= dayStart;
  });
}

/**
 * Group BE per-user capacity & leave data by team for the visible date range.
 *
 * A job is assigned to the whole team, not to individual members, so the
 * team-level figures are NOT a per-member sum:
 * - `availableHours` is a flat 8h/day when the team has any current member,
 *   regardless of headcount or leave (CWL-157) — a team's capacity doesn't
 *   grow with more people or shrink when one of them is off.
 * - `bookedHours` is the largest of the members' `capacitySlots[].bookedHours`
 *   for that day, since every current member's slot already carries the same
 *   team bookings (one `schedule_member` row per member per job) — taking the
 *   max avoids multiplying the true hours by headcount.
 *
 * Per-member figures (`membersByTeam`) are unaffected: each member's own
 * `bookedHours`/`availableHours` still reflect their individual data.
 */
export function useTeamCapacities(
  teamIds: string[],
  rangeStart: Date,
  rangeEndExclusive: Date,
): UseTeamCapacitiesResult {
  const { data: resources = [] } = usePlannerResources();

  const startIso = rangeStart.toISOString();
  const endIso = rangeEndExclusive.toISOString();

  const userIds = useMemo(
    () => resources.filter((r) => r.teamId != null).map((r) => r.id),
    [resources],
  );

  const { data: availability = [], isLoading } = useResourceAvailabilityFull(
    startIso,
    endIso,
    userIds.length > 0 ? userIds : undefined,
  );

  return useMemo(() => {
    // Group users by team
    const teamMembers = new Map<string, PlannerResource[]>();
    for (const resource of resources) {
      if (resource.teamId == null) continue;
      const key = String(resource.teamId);
      const list = teamMembers.get(key) ?? [];
      list.push(resource);
      teamMembers.set(key, list);
    }

    // Index BE response by userId
    const availabilityByUser = new Map<number, (typeof availability)[number]>();
    for (const entry of availability) {
      availabilityByUser.set(entry.userId, entry);
    }

    const totalDays = Math.max(
      1,
      Math.round((rangeEndExclusive.getTime() - rangeStart.getTime()) / (24 * 60 * 60 * 1000)),
    );

    const capacitiesByTeam = new Map<string, Map<string, TeamDailyCapacity>>();
    const summariesByTeam = new Map<string, TeamCapacitySummary>();
    const membersByTeam = new Map<string, MemberCapacity[]>();

    for (const teamId of teamIds) {
      const members = teamMembers.get(teamId) ?? [];
      const daily = new Map<string, TeamDailyCapacity>();

      // Per-member accumulators, keyed by userId for the duration of this team's loop.
      const memberDailyByUser = new Map<number, Map<string, MemberDailyCapacity>>();
      const memberTotalsByUser = new Map<
        number,
        { booked: number; available: number; leaveDays: number }
      >();
      for (const member of members) {
        memberDailyByUser.set(member.id, new Map());
        memberTotalsByUser.set(member.id, { booked: 0, available: 0, leaveDays: 0 });
      }

      let teamBooked = 0;
      let teamAvailable = 0;
      let overDays = 0;

      for (let i = 0; i < totalDays; i++) {
        const date = addDays(rangeStart, i);
        const dateStr = format(date, 'yyyy-MM-dd');

        let teamBookedForDay = 0;
        const onLeave: string[] = [];

        for (const member of members) {
          const memberAvailability = availabilityByUser.get(member.id);
          const leaves = memberAvailability?.leaveBlocks ?? [];
          const slot = memberAvailability?.capacitySlots.find(
            (s: CapacitySlot) => s.date === dateStr,
          );

          const memberDaily = memberDailyByUser.get(member.id) ?? new Map();
          const memberTotals = memberTotalsByUser.get(member.id) ?? {
            booked: 0,
            available: 0,
            leaveDays: 0,
          };

          if (isUserOnLeaveOn(date, leaves)) {
            onLeave.push(member.name);
            memberDaily.set(dateStr, {
              date: dateStr,
              bookedHours: 0,
              availableHours: 0,
              utilizationPercent: 0,
              isOnLeave: true,
            });
            memberTotals.leaveDays += 1;
            memberTotalsByUser.set(member.id, memberTotals);
            continue;
          }

          const memberBooked = slot ? slot.bookedHours : 0;
          // A job is assigned to the whole team, not an individual: every
          // current member's capacity slot carries the same team bookings, so
          // the team total is the largest member figure, not their sum (which
          // would multiply the true hours by headcount — CWL-157).
          teamBookedForDay = Math.max(teamBookedForDay, memberBooked);

          const memberUtilPercent =
            HOURS_PER_USER_PER_DAY > 0 ? (memberBooked / HOURS_PER_USER_PER_DAY) * 100 : 0;
          memberDaily.set(dateStr, {
            date: dateStr,
            bookedHours: Math.round(memberBooked * 10) / 10,
            availableHours: HOURS_PER_USER_PER_DAY,
            utilizationPercent: Math.round(memberUtilPercent * 10) / 10,
            isOnLeave: false,
          });
          memberTotals.booked += memberBooked;
          memberTotals.available += HOURS_PER_USER_PER_DAY;
          memberTotalsByUser.set(member.id, memberTotals);
        }

        // Team capacity is a flat 8h/day, independent of headcount and of any
        // one member's leave — the job is assigned to the team, not to its
        // members (CWL-157). A team with no current members has none.
        const availableHours = members.length > 0 ? HOURS_PER_USER_PER_DAY : 0;
        const utilizationPercent =
          availableHours > 0 ? (teamBookedForDay / availableHours) * 100 : 0;
        const isOverallocated = utilizationPercent > 100;

        daily.set(dateStr, {
          date: dateStr,
          totalHours: Math.round(teamBookedForDay * 10) / 10,
          availableHours,
          utilizationPercent: Math.round(utilizationPercent * 10) / 10,
          isOverallocated,
          membersOnLeave: onLeave,
        });

        teamBooked += teamBookedForDay;
        teamAvailable += availableHours;
        if (isOverallocated) overDays += 1;
      }

      const averageUtilization = teamAvailable > 0 ? (teamBooked / teamAvailable) * 100 : 0;

      capacitiesByTeam.set(teamId, daily);
      summariesByTeam.set(teamId, {
        teamId,
        totalBookedHours: Math.round(teamBooked * 10) / 10,
        totalAvailableHours: teamAvailable,
        averageUtilization: Math.round(averageUtilization * 10) / 10,
        isOverallocated: averageUtilization > 100,
        overallocatedDays: overDays,
      });

      const memberCapacities: MemberCapacity[] = members.map((member) => {
        const totals = memberTotalsByUser.get(member.id) ?? {
          booked: 0,
          available: 0,
          leaveDays: 0,
        };
        const avgUtil = totals.available > 0 ? (totals.booked / totals.available) * 100 : 0;
        return {
          userId: member.id,
          userName: member.name,
          teamId,
          daily: memberDailyByUser.get(member.id) ?? new Map(),
          totalBookedHours: Math.round(totals.booked * 10) / 10,
          totalAvailableHours: totals.available,
          averageUtilization: Math.round(avgUtil * 10) / 10,
          isOverallocated: avgUtil > 100,
          isOnLeaveAllRange: totals.leaveDays === totalDays,
        };
      });
      membersByTeam.set(teamId, memberCapacities);
    }

    const summaries = Array.from(summariesByTeam.values());
    const overallAvg =
      summaries.length > 0
        ? summaries.reduce((s, t) => s + t.averageUtilization, 0) / summaries.length
        : 0;
    const teamsOver = summaries.filter((s) => s.isOverallocated).length;

    return {
      capacitiesByTeam,
      summariesByTeam,
      membersByTeam,
      overall: {
        averageUtilization: Math.round(overallAvg * 10) / 10,
        teamsOverCapacity: teamsOver,
        totalTeams: summaries.length,
      },
      isLoading,
    };
  }, [teamIds, rangeStart, rangeEndExclusive, resources, availability, isLoading]);
}
